import { useState, useMemo } from 'react';
import { Download, FileText, AlertCircle } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { FormField, Input, Select } from '../ui/Form';
import { useDataExchange } from '../../hooks/useDataExchange';
import { ACCOUNTING_STANDARD_LABELS, FILE_FORMAT_LABELS, DATA_TYPE_LABELS } from '../../types/data-exchange';
import type { AccountingStandard, FileFormat, DataType, FieldSeparator } from '../../types/data-exchange';
import type { FiscalYear } from '../../types/fiscal-year';

interface ExportDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  fiscalYear: FiscalYear;
}

type ExportMode = 'standard' | 'fec';

// Combinaisons valides norme → formats supportés (selon backend)
const VALID_FORMAT_MAP: Record<string, FileFormat[]> = {
  syscohada: ['csv', 'xlsx'],
  ohada_revise: ['csv', 'xlsx'],
  saf_t: ['xml'],
  xbrl: ['xml'],
  generic: ['csv', 'xlsx', 'json', 'txt'],
};

// Normes comptables disponibles (FEC exclu → mode dédié)
const EXPORT_STANDARDS: { value: AccountingStandard; label: string }[] = [
  { value: 'syscohada', label: ACCOUNTING_STANDARD_LABELS.syscohada },
  { value: 'ohada_revise', label: ACCOUNTING_STANDARD_LABELS.ohada_revise },
  { value: 'saf_t', label: ACCOUNTING_STANDARD_LABELS.saf_t },
  { value: 'xbrl', label: ACCOUNTING_STANDARD_LABELS.xbrl },
  { value: 'generic', label: ACCOUNTING_STANDARD_LABELS.generic },
];

// Types de données exportables
const EXPORT_DATA_TYPES: { value: DataType; label: string }[] = [
  { value: 'journal_entries', label: DATA_TYPE_LABELS.journal_entries },
  { value: 'chart_of_accounts', label: DATA_TYPE_LABELS.chart_of_accounts },
  { value: 'trial_balance', label: DATA_TYPE_LABELS.trial_balance },
  { value: 'general_ledger', label: DATA_TYPE_LABELS.general_ledger },
  { value: 'fiscal_year', label: DATA_TYPE_LABELS.fiscal_year },
];

/**
 * Modal d'export de données comptables d'un exercice fiscal.
 * Supporte l'export standard (multi-format) et l'export FEC spécifique (DGFiP).
 * 
 * L'export standard permet de choisir :
 * - La norme comptable (SYSCOHADA, SAF-T, etc.)
 * - Le format de fichier (CSV, Excel, XML, etc.)
 * 
 * L'export FEC est un cas spécial conforme à la réglementation française.
 */
export function ExportDataModal({
  isOpen,
  onClose,
  fiscalYear
}: ExportDataModalProps) {
  const [mode, setMode] = useState<ExportMode>('standard');
  const [fileFormat, setFileFormat] = useState<FileFormat>('csv');
  const [standard, setStandard] = useState<AccountingStandard>('syscohada');
  const [dataType, setDataType] = useState<DataType>('journal_entries');
  const [encoding, setEncoding] = useState('utf-8');
  const [separator, setSeparator] = useState<FieldSeparator>(';');
  const [compress, setCompress] = useState(false);
  const [siren, setSiren] = useState('');
  const [closingDate, setClosingDate] = useState(fiscalYear.endDate);

  const { loading, error, exportFiscalYear, exportFec } = useDataExchange();

  // Formats de fichier filtrés selon la norme comptable sélectionnée
  const availableFormats = useMemo(() => {
    const allowed = VALID_FORMAT_MAP[standard] || ['csv', 'xlsx', 'json', 'txt', 'xml'];
    return allowed.map(f => ({ value: f, label: FILE_FORMAT_LABELS[f] }));
  }, [standard]);

  // Quand la norme change, auto-sélectionner le 1er format valide si le format actuel est invalide
  const handleStandardChange = (newStandard: AccountingStandard) => {
    setStandard(newStandard);
    const allowed = VALID_FORMAT_MAP[newStandard] || ['csv'];
    if (!allowed.includes(fileFormat)) {
      setFileFormat(allowed[0]);
    }
  };

  // Le séparateur est pertinent pour CSV et TXT uniquement
  const showSeparator = fileFormat === 'csv' || fileFormat === 'txt';

  const handleExport = async () => {
    let success = false;
    if (mode === 'fec') {
      if (!siren || siren.length !== 9) return;
      success = await exportFec(fiscalYear.id, siren, closingDate);
    } else {
      success = await exportFiscalYear(fiscalYear.id, {
        fileFormat,
        accountingStandard: standard,
        dataType,
        encoding,
        separator: showSeparator ? separator : undefined,
        compress,
        startDate: fiscalYear.startDate,
        endDate: fiscalYear.endDate,
      });
    }
    if (success) onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Exporter les données comptables"
      size="md"
    >
      <div className="space-y-5">
        {/* Exercice */}
        <div className="flex items-center gap-2 text-sm bg-muted/50 rounded-md px-3 py-2">
          <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="text-muted-foreground">Exercice :</span>
          <span className="font-medium text-foreground">{fiscalYear.code}</span>
          <span className="text-muted-foreground">
            ({new Date(fiscalYear.startDate).toLocaleDateString('fr-FR')} — {new Date(fiscalYear.endDate).toLocaleDateString('fr-FR')})
          </span>
        </div>

        {/* Mode */}
        <div className="flex gap-2">
          <Button
            variant={mode === 'standard' ? 'primary' : 'secondary'}
            onClick={() => setMode('standard')}
            size="sm"
          >
            Export standard
          </Button>
          <Button
            variant={mode === 'fec' ? 'primary' : 'secondary'}
            onClick={() => setMode('fec')}
            size="sm"
          >
            Export FEC (DGFiP)
          </Button>
        </div>

        {mode === 'standard' ? (
          <div className="space-y-4">
            <FormField label="Type de données">
              <Select value={dataType} onChange={(e) => setDataType(e.target.value as DataType)}>
                {EXPORT_DATA_TYPES.map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </Select>
            </FormField>

            <FormField label="Norme comptable">
              <Select value={standard} onChange={(e) => handleStandardChange(e.target.value as AccountingStandard)}>
                {EXPORT_STANDARDS.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </Select>
            </FormField>

            <FormField label="Format de fichier">
              <Select value={fileFormat} onChange={(e) => setFileFormat(e.target.value as FileFormat)}>
                {availableFormats.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </Select>
              {(standard === 'saf_t' || standard === 'xbrl') && (
                <p className="text-xs text-muted-foreground mt-1">
                  La norme {ACCOUNTING_STANDARD_LABELS[standard]} requiert le format XML.
                </p>
              )}
            </FormField>

            {showSeparator && (
              <FormField label="Séparateur de champs">
                <Select value={separator} onChange={(e) => setSeparator(e.target.value as FieldSeparator)}>
                  <option value=";">Point-virgule ( ; )</option>
                  <option value=",">Virgule ( , )</option>
                  <option value="|">Pipe ( | )</option>
                  <option value={'\t'}>Tabulation</option>
                </Select>
              </FormField>
            )}

            <FormField label="Encodage">
              <Select value={encoding} onChange={(e) => setEncoding(e.target.value)}>
                <option value="utf-8">UTF-8</option>
                <option value="iso-8859-1">ISO-8859-1 (Latin-1)</option>
                <option value="windows-1252">Windows-1252</option>
              </Select>
            </FormField>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={compress}
                onChange={(e) => setCompress(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-foreground">Compresser (Gzip)</span>
            </label>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <div className="flex items-start gap-2 text-sm text-blue-700 dark:text-blue-400">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Export FEC conforme DGFiP</p>
                  <p className="text-xs mt-1">
                    Génère un fichier au format réglementaire français (Fichier des Écritures Comptables).
                    Le nom du fichier suit la norme : {'{SIREN}'}FEC{'{AAAAMMJJ}'}.txt
                  </p>
                </div>
              </div>
            </div>

            <FormField label="N° SIREN (9 chiffres)" required>
              <Input
                value={siren}
                onChange={(e) => setSiren(e.target.value.replace(/\D/g, '').slice(0, 9))}
                placeholder="123456789"
                maxLength={9}
              />
              {siren.length > 0 && siren.length !== 9 && (
                <p className="text-xs text-red-500 mt-1">Le SIREN doit contenir exactement 9 chiffres</p>
              )}
            </FormField>

            <FormField label="Date de clôture" required>
              <Input
                type="date"
                value={closingDate}
                onChange={(e) => setClosingDate(e.target.value)}
              />
            </FormField>
          </div>
        )}

        {/* Erreur */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button
            onClick={handleExport}
            isLoading={loading}
            icon={Download}
            disabled={mode === 'fec' && siren.length !== 9}
          >
            Exporter
          </Button>
        </div>
      </div>
    </Modal>
  );
}
